import { useMemo } from "react";
import useCustomerContext from "../../customers/hooks/useCustomerContext";
import useBookingSearchContext from "../../booking/hooks/useBookingSearchContext";

interface UseExternalHotelUrlParams {
    externalUrl: string | null;
    internalUrl?: string;
}

/**
 * Hook para construir URLs externas de hoteles con parámetros de búsqueda
 * @param externalUrl - URL externa del hotel (ej: Booking.com)
 * @param internalUrl - URL interna por defecto si no hay URL externa
 * @returns URL completa con parámetros de búsqueda y información de si es un redirect externo
 */
export default function useExternalHotelUrl({ externalUrl, internalUrl = '' }: UseExternalHotelUrlParams) {
    const { customer } = useCustomerContext();
    const { dates, rooms } = useBookingSearchContext();

    const url = useMemo(() => {
        if (externalUrl === null) {
            return internalUrl;
        }

        try {
            // Paso 1: Construir la externalUrl completa con todos sus parámetros
            const externalUrlObj = new URL(externalUrl);
            
            // Añadir parámetros de búsqueda a externalUrl
            if (dates.startDate) {
                externalUrlObj.searchParams.set('checkin', dates.startDate.format('YYYY-MM-DD'));
            }
            if (dates.endDate) {
                externalUrlObj.searchParams.set('checkout', dates.endDate.format('YYYY-MM-DD'));
            }
            
            // Añadir información de habitaciones/huéspedes
            const totalAdults = rooms.reduce((sum, room) => sum + (room.guestAdults || 0), 0);
            const totalChildren = rooms.reduce((sum, room) => sum + (room.guestChildren || 0), 0);
            
            if (totalAdults > 0) {
                externalUrlObj.searchParams.set('group_adults', totalAdults.toString());
            }
            if (totalChildren > 0) {
                externalUrlObj.searchParams.set('group_children', totalChildren.toString());
            }
            if (rooms.length > 0) {
                externalUrlObj.searchParams.set('no_rooms', rooms.length.toString());
            }

            const completeExternalUrl = externalUrlObj.toString();

            // Paso 2: Si existe baseExternalUrl, usarla y añadir externalUrl como parámetro
            if (customer.baseExternalUrl) {
                const baseUrl = new URL(customer.baseExternalUrl);
                baseUrl.searchParams.set('url', completeExternalUrl);
                return baseUrl.toString();
            }

            // Si no hay baseExternalUrl, devolver solo la externalUrl con sus parámetros
            return completeExternalUrl;
        } catch {
            // Fallback a concatenación simple si falla
            return customer.baseExternalUrl 
                ? customer.baseExternalUrl + '?url=' + encodeURIComponent(externalUrl)
                : externalUrl;
        }
    }, [externalUrl, internalUrl, customer, dates, rooms]);

    return {
        url,
        isExternal: externalUrl !== null
    };
}
